<template>
  <div class="app-data-item-reviews-page">
    <div class="container">
      <div class="data-page-top">
        <h1 class="page-title">Member reviews</h1>
      </div>
      <div class="reviews-total">
        <template v-if="!reviewsFetching">{{ memberReviews.length }} total</template>
      </div>
      <div class="reviews-list">
        <div v-if="reviewsFetching" class="loader-wrapper">
          <AppLoader />
        </div>
        <AppReviewableList
          v-else
          :reviews="memberReviews"
          @likes-changed="refetchReviews"
          @reply-success="refetchReviews"
        />
      </div>

      <div v-if="!reviewsFetching" class="add-review-line">
        <button class="border-btn" type="button" @click="addReview">Add review</button>
      </div>

      <CollapseTransition :duration="200">
        <div v-if="showReviewField" class="app-review-form-wrapper">
          <AppReviewableForm autofocus closeable @submit="saveReview" @close="hideReviewField" />
        </div>
      </CollapseTransition>
    </div>
  </div>
</template>

<script>
import { EntityTypes } from '@/helpers/entityType';
import { prepareVariablesForSingleEntityQuery } from '@/helpers/graphqlHelper';
import { getContentTypeFromEntityType } from '@/helpers/ga/params/contentTypeParam';
import { sendGaEvent } from '@/helpers/ga/gaSend';
import MemberReviewAddedGaEvent from '@/helpers/ga/events/MemberReviewAddedGaEvent';
import AppReviewableList from '@/components/AppReviewableList.vue';
import CollapseTransition from '@/components/transitions/CollapseTransition.vue';
import AppReviewableForm from '@/components/AppReviewableForm.vue';
import academyMemberReviewsQuery from '@/graphql/memberReview/AcademyMemberReviews.query.gql';
import artFairMemberReviewsQuery from '@/graphql/memberReview/ArtFairMemberReviews.query.gql';
import artSpaceMemberReviewsQuery from '@/graphql/memberReview/ArtSpaceMemberReviews.query.gql';
import eventMemberReviewsQuery from '@/graphql/memberReview/EventMemberReviews.query.gql';
import exhibitionMemberReviewsQuery from '@/graphql/memberReview/ExhibitionMemberReviews.query.gql';
import experienceMemberReviewsQuery from '@/graphql/memberReview/ExperienceMemberReviews.query.gql';
import miniCultivistMemberReviewsQuery from '@/graphql/memberReview/MiniCultivistMemberReviews.query.gql';
import addMemberReviewMutation from '@/graphql/memberReview/AddMemberReview.mutation.gql';

export default {
  name: 'DataItemReviewsPage',
  components: {
    AppReviewableList,
    CollapseTransition,
    AppReviewableForm,
  },

  metaInfo() {
    let title = this.entity?.title || 'Event';

    if (this.entity?.isPermanentCollection && this.entity?.artSpace) {
      title += `. ${this.entity.artSpace.title}`;
    }

    return {
      title: `Reviews: ${title}`,
    };
  },

  apollo: {
    reviewsEntity: {
      query() {
        if (this.entityType === EntityTypes.academy) {
          return academyMemberReviewsQuery;
        }
        if (this.entityType === EntityTypes.art_fair) {
          return artFairMemberReviewsQuery;
        }
        if (this.entityType === EntityTypes.art_space) {
          return artSpaceMemberReviewsQuery;
        }
        if (this.entityType === EntityTypes.event) {
          return eventMemberReviewsQuery;
        }
        if (this.entityType === EntityTypes.exhibition) {
          return exhibitionMemberReviewsQuery;
        }
        if (this.entityType === EntityTypes.experience) {
          return experienceMemberReviewsQuery;
        }
        if (this.entityType === EntityTypes.mini_cultivist) {
          return miniCultivistMemberReviewsQuery;
        }
      },
      variables() {
        const { slug } = prepareVariablesForSingleEntityQuery(this.$route.params);

        return { slug };
      },
      result(result) {
        if (result.data.reviewsEntity) {
          this.entity = {
            id: result.data.reviewsEntity.id,
            title: result.data.reviewsEntity.title,
            isPermanentCollection: result.data.reviewsEntity.is_permanent_collection || false,
            artSpace: result.data.reviewsEntity.artSpace,
          };
          this.memberReviews = result.data.reviewsEntity.memberReviews;
        }
        this.reviewsFetching = false;
      },
    },
  },

  props: {
    entityType: {
      type: String,
      default: undefined,
    },
  },

  data() {
    return {
      reviewsFetching: true,
      showReviewField: false,
      memberReviews: [],
      entity: null,
    };
  },

  methods: {
    addReview() {
      this.showReviewField = true;
    },

    saveReview(text) {
      this.$apollo
        .mutate({
          mutation: addMemberReviewMutation,
          variables: {
            objectType: this.entityType,
            objectId: this.entity.id,
            text,
          },
        })
        .then(() => {
          this.$toast.warning('Please wait confirmation.');

          const gaEvent = new MemberReviewAddedGaEvent()
            .addContentType(getContentTypeFromEntityType(this.entityType))
            .addContentId(this.entity.id)
            .addContentName(this.entity.title);

          sendGaEvent(gaEvent);
          this.hideReviewField();
        });
    },
    hideReviewField() {
      this.showReviewField = false;
    },
    refetchReviews() {
      this.$apollo.queries.reviewsEntity.refetch();
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-data-item-reviews-page {
  margin-top: 100px;

  .reviews-total {
    margin-bottom: 45px;
    color: vars.$c-dark-gray;
    font-size: 18px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 5px;
    text-align: right;
  }

  .reviews-list {
    margin-bottom: 40px;
  }

  .add-review-line {
    text-align: center;

    button {
      max-width: 410px;
      width: 100%;
    }
  }

  .loader-wrapper {
    text-align: center;
  }

  .app-review-form-wrapper {
    margin-top: 40px;
    overflow: hidden;
  }
}
</style>
